import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import { makeGetTask } from 'selectors/tasks';
import { makeGetTaskGroup } from 'selectors/task-groups';
import { makeGetProject } from 'selectors/projects';

import TaskGroupName from 'components/task-group-name';
import CanUser from 'components/can-user';
import SelectTaskGroupPopout from 'components/SelectTaskGroupPopout';
import OverflowTooltip from 'components/overflow-tooltip';

import IconCheckCircled from 'icons/icon-check-circled';
import IconArrowDown from 'icons/icon-arrow-down';

const TaskTaskGroup = ({ id, taskGroupId, taskGroupColor, project, archived, completed, createdBy, moduleDisabled, withIcon, icon, withColor, withShorten = true, nameFallback, provideController, onChange, onComplete, t }) => {
    const handleTaskGroupSelect = useCallback(({ id: nextTaskGroupId, projectId }) => {
        if (taskGroupId === nextTaskGroupId) {
            return;
        }

        onChange(nextTaskGroupId, projectId);
    }, [taskGroupId, onChange]);

    const handleComplete = useCallback(() => {
        onComplete(true);
    }, []);

    const nameElement = useMemo(() => {
        if (taskGroupId) {
            return <TaskGroupName id={taskGroupId} />;
        }

        return nameFallback;
    }, [taskGroupId, nameFallback]);

    const hasColor = useMemo(() => {
        if (!withColor) {
            return false;
        }

        if (completed) {
            return false;
        }

        if (!taskGroupColor || taskGroupColor === 'transparent') {
            return false;
        }

        return true;
    }, [withColor, completed, taskGroupColor]);

    return (
        <div className={'task-task-group meta-item' + (hasColor ? ' has-color' : '')} style={hasColor ? { borderLeft: `3px solid ${taskGroupColor}` } : undefined }>
            {
                withIcon ? (
                    <span className="meta-icon">
                        { icon || <IconCheckCircled /> }
                    </span>
                ) : null
            }

            {
                moduleDisabled ? t('common:N/A') : (
                    <div className="task-task-group-name">
                        {
                            !archived && !completed ? (
                                <React.Fragment>
                                    <CanUser type="moveTask" customList={createdBy}>
                                        <div className="task-action">
                                            <SelectTaskGroupPopout
                                                title={ t('actions.Set Task Status') }
                                                projectId={project}
                                                selected={taskGroupId}
                                                xAlign="right"
                                                defaultXPosition="left"
                                                onSelect={handleTaskGroupSelect}
                                                provideController={provideController}
                                                onComplete={onComplete ? handleComplete : undefined}
                                            >
                                                <div>
                                                    <OverflowTooltip>
                                                        <span className={'task-cell-text-with-action' + (withShorten ? ' shorten-text shorten-text-180' : '')}>
                                                            { nameElement }
                                                        </span>
                                                    </OverflowTooltip>

                                                    <span className="task-cell-arrow">
                                                        <IconArrowDown size="10px" />
                                                    </span>
                                                </div>
                                            </SelectTaskGroupPopout>
                                        </div>
                                    </CanUser>

                                    <CanUser type="moveTask" customList={createdBy} reverse={true}>
                                        { nameElement }
                                    </CanUser>
                                </React.Fragment>
                            ) : completed ? t('common:Completed') : nameElement
                        }
                    </div>
                )
            }
        </div>
    );
};

const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const getTaskGroup = makeGetTaskGroup();
    const getProject = makeGetProject();

    return ({ tasks, taskGroups, projects }, ownProps) => {
        const task = getTask(tasks.items, ownProps.id);
        const taskGroupId = task?.task_group;
        const taskGroup = getTaskGroup(taskGroups.items, taskGroupId);

        return {
            taskGroupId,
            taskGroupColor: taskGroup?.color,
            project: task?.project,
            archived: task?.archived,
            completed: task?.completed,
            createdBy: task?.created_by,
            moduleDisabled: task?.project && (getProject(projects.items, task.project)?.disabled_modules || []).includes('tasks')
        }
    }
}

export default connect(makeMapStateToProps)(withTranslation(['task', 'common'])(React.memo(TaskTaskGroup)));
